'use client';

import { CardGridProps, CardItem } from '@/components/CardGrid';
import EntryCard from '@/components/EntryCard';
import EntryIndexNoResults from '@/components/EntryIndex/EntryIndex.NoResults';
import { useEntryIndex } from '@/components/EntryIndex/useEntryIndex';
import { Entry } from '@/lib/parsers/entries';
import { FeaturedCard } from '../EntryCard/EntryCard.Featured';
import SkeletonCards, { SkeletonCardsProps } from '../Skeleton/SkeletonCards';
import Grid from '../ui/Grid';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EntryIndexResultsProps<T extends CardItem = any> = {
  CardGridProps?: Partial<CardGridProps<T>>;
  SkeletonCardsProps?: SkeletonCardsProps;
};

/**
 * This component is responsible for rendering the entry index results
 * or no-results message
 */
const EntryIndexResults = <T extends CardItem>(props: EntryIndexResultsProps<T>) => {
  const loading = useEntryIndex((s) => s.loading);
  const initialLoaded = useEntryIndex((s) => s.initialLoaded);
  const items = useEntryIndex((s) => s.items);

  const hasResults = items.length > 0;
  const showLoading = loading && !initialLoaded;

  const skeletonCardsProps = {
    amount: 4,
    md: 6,
    lg: 3,
    cx: { mB: 'lg' },
    SkeletonItems: { items: [{ variant: 'rounded', height: 450 }] },
    ...props.SkeletonCardsProps,
  } as SkeletonCardsProps;

  return (
    <>
      {showLoading && (
        <>
          <SkeletonCards
            amount={1}
            SkeletonItems={{ items: [{ variant: 'rounded', height: 740 }], cx: { mB: 'lg' } }}
          />
          <SkeletonCards {...skeletonCardsProps} />
        </>
      )}
      {hasResults ? (
        <Grid rowGutter={false} cx={{ rowGap: { xs: 'xl', lg: '3xl' } }}>
          {items.map((item, i) => {
            return (
              <Grid.Col key={`article-${i}-${item.id}`} sm={6} md={4} lg={i === 0 ? 12 : 3}>
                {i === 0 ? (
                  <FeaturedCard item={item as Entry<'article'>} />
                ) : (
                  <EntryCard item={item} />
                )}
              </Grid.Col>
            );
          })}
        </Grid>
      ) : (
        initialLoaded && <EntryIndexNoResults />
      )}
    </>
  );
};

export default EntryIndexResults;
